import {
  Add as AddIcon,
  Close as ClearIcon,
  DeleteOutlineOutlined as DeleteIcon,
  DescriptionOutlined as DescriptionOutlinedIcon,
  Download as DownloadIcon,
  ExpandMore as ExpandMoreIcon,
  GroupOutlined as GroupOutlinedIcon,
  Inventory2Outlined as Inventory2OutlinedIcon,
  Search as SearchIcon,
  UnarchiveOutlined as UnarchiveIcon,
} from "@/components/icons";
import * as React from "react";
import { useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  FormControlLabel,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  format,
  isThisWeek,
  isToday,
  isYesterday,
} from "date-fns";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

import { SegmentedControl } from "@/components/SegmentedControl";
import { SessionScopeChip } from "@/features/sessions/SessionScopeChip";
import { DemoSessionCard } from "@/features/demo/DemoSessionCard";
import { DEMO_SESSION_PATH, demoSessionVisible } from "@/api/demoSession";

import {
  useArchiveSession,
  useArchivedSessions,
  useDeleteSession,
  useDocuments,
  useMe,
  useSessions,
  useUnarchiveSession,
} from "@/api/hooks";
import { DocumentFormatIcon } from "@/features/documents/documentIcon";
import { documentDownloadHref } from "@/features/documents/documentLinks";
import { VersionLabel, latestVersions } from "@/features/documents/DocumentList";
import { useConfirm } from "@/hooks/useConfirm";
import type { SessionSummary } from "@/api/types";
import { apiErrorText } from "@/features/sessions/errorMessages";
import { outputLanguageShort } from "@/features/sessions/outputLanguage";
import { relativeAge } from "@/i18n/format";
import { useNow } from "@/hooks/useNow";
import { brand } from "@/theme";
import { repoTokens } from "@/theme/tokens";
import { displaySessionTitle, titleMatches } from "@/features/sessions/sessionTitle";
import { contributorLabel, userLabel } from "@/lib/userLabel";

type Bucket = "today" | "yesterday" | "week" | "earlier";

const BUCKET_ORDER: Bucket[] = ["today", "yesterday", "week", "earlier"];
const GROUP_STORAGE_KEY = "rw.sidebar.group";
const ARCHIVED_STORAGE_KEY = "rw.sidebar.includeArchived";
// The typed filter and the project pick last for the browser tab
// (sessionStorage), unlike the view choices above: the list unmounts
// while the admin menu holds the sidebar, and "Back to sessions" should
// bring back the list as the viewer left it, not only the session.
const QUERY_TAB_KEY = "rw.sidebar.query";
const PROJECT_TAB_KEY = "rw.sidebar.project";

function readTabQuery(): string {
  try {
    return sessionStorage.getItem(QUERY_TAB_KEY) ?? "";
  } catch {
    return "";
  }
}

function readTabProjectFilter(): ProjectBranchKey | null {
  try {
    const raw = sessionStorage.getItem(PROJECT_TAB_KEY);
    if (!raw) return null;
    const value = JSON.parse(raw) as { path?: unknown; branch?: unknown };
    if (typeof value.path !== "string") return null;
    if (value.branch !== null && typeof value.branch !== "string") return null;
    // ``count`` only orders the options; the filter matches on the pair.
    return { path: value.path, branch: value.branch, count: 0 };
  } catch {
    return null;
  }
}

function writeTab(key: string, value: string | null): void {
  try {
    if (value) sessionStorage.setItem(key, value);
    else sessionStorage.removeItem(key);
  } catch {
    /* private mode / blocked storage: the filter just won't come back */
  }
}

type ShareFilter = "all" | "owned" | "shared";
type GroupMode = "time" | "intent" | "project";
type DisplayGroup = {
  key: string;
  label: string;
  sessions: SessionSummary[];
};

export function SessionList(): React.JSX.Element {
  const { data, isLoading, isError, error, refetch } = useSessions();
  const me = useMe();
  const currentUserId = me.data?.user.id ?? null;
  // Rows carry their org in the meta line only when the viewer can
  // actually see sessions from several orgs — multi-org members, or
  // global admins browsing the cross-org view.
  const scope = me.data?.admin_scope;
  const multiOrgViewer = Boolean(
    scope &&
      (scope.is_global_admin ||
        new Set([
          ...scope.org_member_slugs,
          ...scope.org_admin_slugs,
          ...scope.org_manager_slugs,
        ]).size > 1),
  );
  const location = useLocation();
  const [query, setQuery] = useState(readTabQuery);
  React.useEffect(() => writeTab(QUERY_TAB_KEY, query), [query]);
  const [includeArchived, setIncludeArchived] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(ARCHIVED_STORAGE_KEY) === "1";
  });
  const archived = useArchivedSessions(includeArchived);
  const setIncludeArchivedPersist = (next: boolean) => {
    setIncludeArchived(next);
    try {
      window.localStorage.setItem(ARCHIVED_STORAGE_KEY, next ? "1" : "0");
    } catch {
      /* quota / private-mode — no-op */
    }
  };
  // Three-segment ownership filter — pure client-side, applied after
  // text + project filters. "shared" hides solo sessions (no
  // collaborators); "owned" hides sessions where the viewer isn't
  // the owner. Persisted alongside the sort pref so opening a new
  // tab restores the view the user expects.
  const [shareFilter, setShareFilter] = useState<ShareFilter>(() => {
    if (typeof window === "undefined") return "all";
    const raw = window.localStorage.getItem("rw.sidebar.share");
    if (raw === "all" || raw === "owned" || raw === "shared") return raw;
    return "all";
  });
  const setShareFilterPersist = (next: ShareFilter) => {
    setShareFilter(next);
    try {
      window.localStorage.setItem("rw.sidebar.share", next);
    } catch {
      /* quota / private-mode — no-op */
    }
  };
  // "group/project · branch" filter. Populated from the
  // project/branch combinations already present on the user's
  // sessions, so the user can jump back to yesterday's session
  // graph without remembering exact phrasing. ``null`` means no
  // filter applied — the text filter still narrows on top.
  const [projectFilter, setProjectFilter] = useState<ProjectBranchKey | null>(
    readTabProjectFilter,
  );
  React.useEffect(
    () =>
      writeTab(
        PROJECT_TAB_KEY,
        projectFilter
          ? JSON.stringify({ path: projectFilter.path, branch: projectFilter.branch })
          : null,
      ),
    [projectFilter],
  );
  // Chrome's saved-password autofill can fill the last visible text
  // input on a page without focusing it, which fires this
  // Autocomplete's onInputChange and would otherwise pop the
  // listbox open behind the user's back (seen on Account settings,
  // whose formless password fields make Chrome misidentify this
  // field as the "username"). Controlling ``open`` lets us ignore a
  // "change" event whose target isn't the currently focused
  // element — a real user typing, clicking or using arrow keys
  // while focused still opens it normally.
  const [projectAutocompleteOpen, setProjectAutocompleteOpen] =
    useState(false);
  // Sort mode persists in localStorage so the user's pick survives
  // reloads / new-tab opens. Default "recent" preserves prior
  // behaviour; falling back when the stored value is stale or
  // typo'd keeps a corrupted localStorage from crashing the
  // sidebar.
  const [sortMode, setSortMode] = useState<SortMode>(() => {
    if (typeof window === "undefined") return "recent";
    const raw = window.localStorage.getItem("rw.sidebar.sort");
    if (raw === "recent" || raw === "title" || raw === "documents") return raw;
    return "recent";
  });
  const setSortModePersist = (next: SortMode) => {
    setSortMode(next);
    try {
      window.localStorage.setItem("rw.sidebar.sort", next);
    } catch {
      /* quota / private-mode — no-op */
    }
  };
  const [groupMode, setGroupMode] = useState<GroupMode>(() => {
    if (typeof window === "undefined") return "time";
    const raw = window.localStorage.getItem(GROUP_STORAGE_KEY);
    if (raw === "time" || raw === "intent" || raw === "project") return raw;
    return "time";
  });
  const setGroupModePersist = (next: GroupMode) => {
    setGroupMode(next);
    try {
      window.localStorage.setItem(GROUP_STORAGE_KEY, next);
    } catch {
      /* quota / private-mode — no-op */
    }
  };
  const { t } = useTranslation();

  const allSessions = useMemo(() => {
    const live = data ?? [];
    if (!includeArchived) return live;
    return [...live, ...(archived.data ?? [])];
  }, [data, includeArchived, archived.data]);

  const projectBranchOptions = useMemo(
    () => deriveProjectBranchOptions(allSessions),
    [allSessions],
  );

  const activeProjectFilter = effectiveProjectFilter(
    projectFilter,
    projectBranchOptions,
  );
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let pool = allSessions;
    if (activeProjectFilter) {
      pool = pool.filter((s) => matchesProjectBranch(s, activeProjectFilter));
    }
    pool = applyShareFilter(pool, shareFilter, currentUserId);
    if (!q) return pool;
    return pool.filter((s) =>
      matchesQuery(
        s,
        q,
        (slug: string) => t(`intents.${slug}`, { defaultValue: slug }),
        t,
      ),
    );
  }, [allSessions, query, activeProjectFilter, shareFilter, currentUserId, t]);

  const sorted = useMemo(() => sortSessions(filtered, sortMode), [filtered, sortMode]);
  const displayGroups = useMemo(
    () =>
      groupSessions(sorted, groupMode, {
        bucketLabel: (bucket) => t(`sidebar.buckets.${bucket}`),
        intentLabel: (slug) => t(`intents.${slug}`, { defaultValue: slug }),
        unlinkedProject: t("sidebar.unlinked_project", "Unlinked project"),
      }),
    [sorted, groupMode, t],
  );
  const loading = isLoading || (includeArchived && archived.isLoading);
  // The demo session (FW-34) heads the list until the user hides it. A
  // text or project filter narrows to real sessions and the Shared view to
  // shared ones, so those leave it out; under Mine it stays, as the
  // user's own.
  const showDemo =
    demoSessionVisible(me.data) &&
    !query.trim() &&
    !activeProjectFilter &&
    shareFilter !== "shared";

  return (
    <Box
      sx={{
        // The left column's inner margin (the shell's footer row below
        // uses the same).
        p: `${repoTokens.shell.leftColumnPadding}px`,
        overflow: "auto",
        flex: 1,
        minHeight: 0,
        // Reserve the scrollbar's gutter up front: the controls keep one
        // width whether or not the list scrolls, instead of jumping (and
        // the toggle groups' right edge meeting the scrollbar) once a
        // fourth session makes it scroll.
        scrollbarGutter: "stable",
      }}
    >
      <Stack
        direction="row"
        sx={{ alignItems: "center", gap: 0.75, mb: 1 }}
      >
        <Button
          component={Link}
          to="/app/sessions"
          variant="contained"
          startIcon={<AddIcon fontSize="small" />}
          fullWidth
          sx={{ minWidth: 0 }}
        >
          {t("common.new_session")}
        </Button>
      </Stack>
      <Stack direction="row" sx={{ alignItems: "center", gap: 0.75, mb: 1 }}>
        <TextField
          size="small"
          fullWidth
          placeholder={t("common.filter_sessions")}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: query ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label={t("session.clear_filter_aria")}
                    onClick={() => setQuery("")}
                    edge="end"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            },
          }}
          sx={{ flex: 1, minWidth: 0 }}
        />
        <SessionScopeChip />
      </Stack>
      {projectBranchOptions.length > 0 && (
        <Autocomplete<ProjectBranchKey>
          size="small"
          fullWidth
          open={projectAutocompleteOpen}
          onOpen={(e) => {
            if (
              e.type === "change" &&
              e.target !== document.activeElement
            ) {
              return;
            }
            setProjectAutocompleteOpen(true);
          }}
          onClose={() => setProjectAutocompleteOpen(false)}
          options={projectBranchOptions}
          value={activeProjectFilter}
          onChange={(_e, option) => setProjectFilter(option)}
          getOptionLabel={(opt) => `${opt.path} · ${opt.branch ?? "—"}`}
          isOptionEqualToValue={(a, b) =>
            a.path === b.path && a.branch === b.branch
          }
          renderOption={(props, opt) => (
            <li {...props} key={`${opt.path}:${opt.branch ?? ""}`}>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="body2" noWrap sx={{ fontFamily: "monospace" }}>
                  {opt.path}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {opt.branch ?? "—"}
                  {opt.count > 1 ? ` · ${opt.count} sessions` : ""}
                </Typography>
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={t(
                "sidebar.project_filter_placeholder",
                "Project / branch…",
              )}
            />
          )}
          sx={{ mb: 1 }}
        />
      )}
      <FormControlLabel
        control={
          <Checkbox
            size="small"
            checked={includeArchived}
            onChange={(e) => setIncludeArchivedPersist(e.target.checked)}
          />
        }
        label={
          <Typography variant="caption">
            {t("sidebar.include_archived", "Include archived")}
          </Typography>
        }
        sx={{
          mx: 0,
          mb: 0.5,
          display: "flex",
          alignItems: "center",
          "& .MuiFormControlLabel-label": { minWidth: 0 },
        }}
      />
      {/* Ownership filter — three segments mirror the sort toggle
          below for visual consistency. Sits above sort because
          "what's in this list" is a coarser decision than how it's
          ordered. ``GroupOutlinedIcon`` only on the "shared" tile so
          the active state visually telegraphs "you're looking at
          collaborative sessions". */}
      <Stack
        direction="row"
        sx={{ mb: 1, gap: 0.5, alignItems: "center" }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ flex: 1, pl: 0.5 }}
        >
          {t("sidebar.share_label", "Show")}
        </Typography>
        <SegmentedControl
          value={shareFilter}
          onChange={(v) => setShareFilterPersist(v)}
          aria-label={t("sidebar.share_label", "Show")}
          options={[
            { value: "all", label: t("session.shared.filter_all", "All") },
            { value: "owned", label: t("session.shared.filter_owned", "Mine") },
            {
              value: "shared",
              label: t("session.shared.filter_shared", "Shared"),
              icon: <GroupOutlinedIcon sx={{ fontSize: 13, mr: 0.5 }} />,
            },
          ]}
        />
      </Stack>
      <Stack
        direction="row"
        sx={{ mb: 1, gap: 0.5, alignItems: "center" }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ flex: 1, pl: 0.5 }}
        >
          {t("sidebar.group_label", "Group")}
        </Typography>
        <SegmentedControl
          value={groupMode}
          onChange={(v) => setGroupModePersist(v)}
          aria-label={t("sidebar.group_label", "Group")}
          options={[
            { value: "time", label: t("sidebar.group_time", "Time") },
            { value: "intent", label: t("sidebar.group_intent", "Intent") },
            { value: "project", label: t("sidebar.group_project", "Project") },
          ]}
        />
      </Stack>
      {/* Sort toggle — compact three-segment control, persists to
          localStorage. Keeps the bucket grouping (today / yesterday
          / week / earlier) and reorders within each bucket. */}
      <Stack
        direction="row"
        sx={{ mb: 1, gap: 0.5, alignItems: "center" }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ flex: 1, pl: 0.5 }}
        >
          {t("sidebar.sort_label", "Sort")}
        </Typography>
        <SegmentedControl
          value={sortMode}
          onChange={(v) => setSortModePersist(v)}
          aria-label={t("sidebar.sort_label", "Sort")}
          options={[
            {
              value: "recent",
              label: t("sidebar.sort_recent", "Recent"),
              "aria-label": t("sidebar.sort_recent", "Recent"),
            },
            {
              value: "title",
              label: t("sidebar.sort_title", "Title"),
              "aria-label": t("sidebar.sort_title", "Title"),
            },
            {
              value: "documents",
              label: t("sidebar.sort_documents", "Docs"),
              "aria-label": t("sidebar.sort_documents", "Documents"),
            },
          ]}
        />
      </Stack>
      {loading && (
        <Stack sx={{ p: 1, gap: 1 }}>
          <Skeleton height={64} />
          <Skeleton height={64} />
          <Skeleton height={64} />
        </Stack>
      )}
      {isError && (
        <Alert
          severity="error"
          sx={{ mx: 1, my: 1 }}
          action={
            <Button color="inherit" size="small" onClick={() => void refetch()}>
              {t("common.retry", "Retry")}
            </Button>
          }
        >
          {apiErrorText(t, error)}
        </Alert>
      )}
      {includeArchived && archived.isError && (
        <Alert severity="warning" sx={{ mx: 1, my: 1 }}>
          {apiErrorText(t, archived.error)}
        </Alert>
      )}
      {/* No "No sessions yet" under the demo card: the list isn't empty. */}
      {!loading && !isError && filtered.length === 0 && (allSessions.length > 0 || !showDemo) && (
        allSessions.length > 0 ? (
          // Sessions exist but the query/filters hide them all — say
          // so, and give a one-click way out. A persisted "Shared"
          // segment or a stale search must never masquerade as an
          // empty account.
          <Stack sx={{ px: 1, py: 2, gap: 1, alignItems: "flex-start" }}>
            <Typography variant="caption" color="text.secondary">
              {query
                ? t("session.no_sessions_match", { query })
                : t(
                    "session.no_sessions_filtered",
                    "Your filters hide all {{count}} sessions.",
                    { count: allSessions.length },
                  )}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setQuery("");
                setProjectFilter(null);
                setShareFilterPersist("all");
              }}
            >
              {t("session.clear_filters", "Clear filters")}
            </Button>
          </Stack>
        ) : (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", px: 1, py: 2 }}
          >
            {t("session.no_sessions_yet")}
          </Typography>
        )
      )}
      {showDemo && (
        <List dense disablePadding sx={{ mt: 1 }}>
          <DemoSessionCard selected={location.pathname.endsWith(DEMO_SESSION_PATH)} />
        </List>
      )}
      {displayGroups.map((group) => {
        const rows = group.sessions;
        return (
          <Box key={group.key} sx={{ mt: 1 }}>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ px: 1, display: "block", lineHeight: 1.8 }}
            >
              {group.label}{" "}
              <Typography component="span" variant="caption" sx={{ opacity: 0.6 }}>
                · {rows.length}
              </Typography>
            </Typography>
            <List dense disablePadding>
              {rows.map((s) => (
                <SessionCard
                  key={s.id}
                  session={s}
                  selected={location.pathname.endsWith(`/sessions/${s.id}`)}
                  currentUserId={currentUserId}
                  showOrg={multiOrgViewer}
                />
              ))}
            </List>
          </Box>
        );
      })}
    </Box>
  );
}

function bucketFor(session: SessionSummary): Bucket {
  const raw = session.last_message_at ?? session.updated_at ?? session.created_at;
  const d = new Date(raw);
  if (isToday(d)) return "today";
  if (isYesterday(d)) return "yesterday";
  if (isThisWeek(d, { weekStartsOn: 1 })) return "week";
  return "earlier";
}

/** Sidebar sort options. "recent" is the default chronological order
 *  the API returns; "title" + "documents" reorder within each time
 *  bucket so the user who scans "where did I do a lot of work" or
 *  "session with <specific> title" finds it faster. */
export type SortMode = "recent" | "title" | "documents";

/** Ownership filter over the merged pool. ``currentUserId === null``
 *  means /me hasn't resolved (or failed) — apply NO filter then, since
 *  classifying rows against an unknown identity marks everything
 *  not-mine and blanks the sidebar for users with a persisted
 *  "Mine"/"Shared" segment. */
export function applyShareFilter(
  rows: SessionSummary[],
  filter: ShareFilter,
  currentUserId: string | null,
): SessionSummary[] {
  if (filter === "all" || currentUserId === null) return rows;
  return rows.filter((s) => {
    const ownedByMe = s.owner?.id === currentUserId;
    const isShared = (s.collaborators?.length ?? 0) > 0;
    if (filter === "owned") return ownedByMe;
    // "shared" → sessions visible to me where I'm not the sole
    // writer: my sessions with collaborators AND sessions someone
    // else shared with me.
    return isShared || !ownedByMe;
  });
}

/** A project filter whose (path, branch) no longer exists in the
 *  options (e.g. it only occurred on archived rows and "Include
 *  archived" was unticked) would keep filtering the pool to empty
 *  AFTER its Autocomplete unmounted — ignore it instead. */
export function effectiveProjectFilter(
  filter: ProjectBranchKey | null,
  options: ProjectBranchKey[],
): ProjectBranchKey | null {
  if (!filter) return null;
  const alive = options.some(
    (o) => o.path === filter.path && o.branch === filter.branch,
  );
  return alive ? filter : null;
}

export function sortSessions(rows: SessionSummary[], mode: SortMode): SessionSummary[] {
  const out = [...rows];
  if (mode === "recent") {
    // Explicitly sort by the backend's COALESCE(last_message_at,
    // updated_at, created_at) instead of trusting API order — the
    // pool is [...live, ...archived] when "Include archived" is on,
    // and each list is only sorted within itself.
    out.sort((a, b) => {
      const aRecent = a.last_message_at ?? a.updated_at ?? a.created_at;
      const bRecent = b.last_message_at ?? b.updated_at ?? b.created_at;
      return (bRecent ?? "").localeCompare(aRecent ?? "");
    });
    return out;
  }
  if (mode === "title") {
    out.sort((a, b) =>
      (a.title || "").localeCompare(b.title || "", undefined, {
        sensitivity: "base",
      }),
    );
    return out;
  }
  // documents: descending by doc count, then most-recent activity
  // as the explicit tiebreak. Returning 0 on tiebreak relies on
  // ``Array.prototype.sort`` being stable — modern engines are, but
  // making the intent explicit keeps the order deterministic across
  // refactors that re-shape ``rows`` upstream.
  out.sort((a, b) => {
    const diff = (b.document_count ?? 0) - (a.document_count ?? 0);
    if (diff !== 0) return diff;
    const aRecent = a.last_message_at ?? a.updated_at ?? a.created_at;
    const bRecent = b.last_message_at ?? b.updated_at ?? b.created_at;
    return (bRecent ?? "").localeCompare(aRecent ?? "");
  });
  return out;
}

function groupByBucket(rows: SessionSummary[]): Record<Bucket, SessionSummary[]> {
  const out: Record<Bucket, SessionSummary[]> = {
    today: [],
    yesterday: [],
    week: [],
    earlier: [],
  };
  for (const r of rows) out[bucketFor(r)].push(r);
  return out;
}

function groupSessions(
  rows: SessionSummary[],
  mode: GroupMode,
  labels: {
    bucketLabel: (bucket: Bucket) => string;
    intentLabel: (slug: string) => string;
    unlinkedProject: string;
  },
): DisplayGroup[] {
  if (mode === "time") {
    const buckets = groupByBucket(rows);
    return BUCKET_ORDER.flatMap((bucket) => {
      const sessions = buckets[bucket];
      if (sessions.length === 0) return [];
      return [{ key: `time:${bucket}`, label: labels.bucketLabel(bucket), sessions }];
    });
  }
  const groups = new Map<string, DisplayGroup>();
  for (const session of rows) {
    const key =
      mode === "intent"
        ? session.intent || "unknown"
        : projectGroupKey(session) ?? "__unlinked__";
    const label =
      mode === "intent"
        ? labels.intentLabel(session.intent || "unknown")
        : projectGroupKey(session) ?? labels.unlinkedProject;
    const existing = groups.get(key);
    if (existing) {
      existing.sessions.push(session);
    } else {
      groups.set(key, {
        key: `${mode}:${key}`,
        label,
        sessions: [session],
      });
    }
  }
  return Array.from(groups.values()).sort((a, b) => {
    if (a.key.endsWith("__unlinked__")) return 1;
    if (b.key.endsWith("__unlinked__")) return -1;
    return a.label.localeCompare(b.label, undefined, { sensitivity: "base" });
  });
}

function projectGroupKey(session: SessionSummary): string | null {
  if (session.repository?.path_with_namespace) {
    return session.repository.path_with_namespace;
  }
  const firstContext = session.context_project_refs?.find(
    (ref) => ref.path_with_namespace,
  );
  return firstContext?.path_with_namespace ?? null;
}

export function metaLine(
  session: SessionSummary,
  intentLabel: string,
  showOrg = false,
): string {
  const parts: string[] = [intentLabel];
  if (session.mode) parts.push(session.mode);
  if (session.language && session.language !== "en") {
    parts.push(outputLanguageShort(session.language));
  }
  // Only meaningful for viewers who can see several orgs at once
  // (multi-org members, admins in the cross-org view) — for everyone
  // else it repeats the same slug on every row.
  if (showOrg && session.org_slug) parts.push(session.org_slug);
  return parts.join(" · ");
}

export function matchesQuery(
  s: SessionSummary,
  q: string,
  intentLabel: (slug: string) => string,
  /** Also match the title as shown (the placeholder in the UI language). */
  t?: TFunction,
): boolean {
  if (t ? titleMatches(t, s.title, q) : s.title.toLowerCase().includes(q)) return true;
  if (s.repository?.path_with_namespace.toLowerCase().includes(q)) return true;
  if (s.branch_name?.toLowerCase().includes(q)) return true;
  if (s.intent?.toLowerCase().includes(q)) return true;
  // Also match the translated intent label so typing
  // "documentation" finds ``document`` sessions.
  if (s.intent && intentLabel(s.intent).toLowerCase().includes(q)) return true;
  if (s.status.toLowerCase().includes(q)) return true;
  // Org slug — in the cross-org "all organisations" view, typing a
  // tenant name is the natural way to narrow.
  if (s.org_slug?.toLowerCase().includes(q)) return true;
  return false;
}

/** One entry in the historical project/branch filter — represents a
 *  (path, branch) pair that shows up on at least one session in the
 *  user's current list. ``count`` drives the secondary "N sessions"
 *  hint under the branch name and the sort order (most-used first). */
export type ProjectBranchKey = {
  path: string;
  branch: string | null;
  count: number;
};

export function deriveProjectBranchOptions(
  rows: SessionSummary[],
): ProjectBranchKey[] {
  // Primary repo + every context_project_ref counts as a
  // combination — a session with two linked projects contributes
  // two entries. That matches what the user "has utilized
  // historically": they picked both and both deserve to be
  // reachable in one click.
  const buckets = new Map<string, ProjectBranchKey>();
  const bump = (path: string, branch: string | null) => {
    const key = `${path}::${branch ?? ""}`;
    const existing = buckets.get(key);
    if (existing) {
      existing.count += 1;
    } else {
      buckets.set(key, { path, branch, count: 1 });
    }
  };
  for (const s of rows) {
    if (s.repository) {
      bump(s.repository.path_with_namespace, s.branch_name);
    }
    for (const ref of s.context_project_refs ?? []) {
      bump(ref.path_with_namespace, ref.branch_name);
    }
  }
  return Array.from(buckets.values()).sort((a, b) => {
    // Most-used first, then alphabetical on path, then branch.
    if (b.count !== a.count) return b.count - a.count;
    const p = a.path.localeCompare(b.path, undefined, { sensitivity: "base" });
    if (p !== 0) return p;
    return (a.branch ?? "").localeCompare(b.branch ?? "", undefined, {
      sensitivity: "base",
    });
  });
}

export function matchesProjectBranch(
  s: SessionSummary,
  key: ProjectBranchKey,
): boolean {
  const matches = (path: string | null | undefined, branch: string | null | undefined) =>
    (path ?? null) === key.path && (branch ?? null) === key.branch;
  if (matches(s.repository?.path_with_namespace, s.branch_name)) return true;
  for (const ref of s.context_project_refs ?? []) {
    if (matches(ref.path_with_namespace, ref.branch_name)) return true;
  }
  return false;
}

function SessionCard({
  session,
  selected,
  currentUserId,
  showOrg,
}: {
  session: SessionSummary;
  selected: boolean;
  showOrg: boolean;
  /** Viewer's user id. Used to render "Owner: You" when the viewer
   *  owns the session, and to gate the "Shared" badge to other
   *  people's collaborative sessions. ``null`` while ``/me`` is
   *  loading — the card just falls back to display names in that
   *  window. */
  currentUserId?: string | null;
}): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const archive = useArchiveSession();
  const unarchive = useUnarchiveSession();
  const deleteSession = useDeleteSession();
  const [docsOpen, setDocsOpen] = useState(false);
  const collaborators = session.collaborators ?? [];
  const isShared = collaborators.length > 0;
  const ownedByMe = !!currentUserId && session.owner?.id === currentUserId;
  const ownerLabel =
    session.owner === null
      ? null
      : ownedByMe
        ? t("session.author.you", "You")
        : userLabel(t, session.owner);
  // ``contributors`` is a Postgres JSONB list of display names
  // appended on every write. The last entry is the most-recent
  // human writer — exactly what we want to surface on the card so
  // a glance at the sidebar tells you who touched it last.
  // Filter out empty strings + dedupe so a long-running session
  // doesn't show a stale "(unknown)" entry.
  const lastActor = (() => {
    const list = (session.contributors ?? []).filter(
      (n): n is string => typeof n === "string" && n.trim().length > 0,
    );
    if (list.length === 0) return null;
    const last = list[list.length - 1];
    // Suppress when last == owner — redundant with the "Owner: …"
    // line. Only worth showing when SOMEONE ELSE was last.
    if (
      session.owner &&
      (last === session.owner.display_name || last === session.owner.email)
    ) {
      return null;
    }
    return last === undefined ? null : contributorLabel(t, last);
  })();
  // Only fetch documents when the user expands; prevents a fan-out of
  // queries across every sidebar item on mount.
  const documents = useDocuments(docsOpen ? session.id : undefined);

  // The list only re-renders on data changes (a new message, an SSE
  // event), so without this tick the age would freeze at whatever it
  // read on the last render — ``useNow`` keeps it advancing.
  const now = useNow();
  const rawTs = session.last_message_at ?? session.updated_at ?? session.created_at;
  const d = new Date(rawTs);
  const rel = relativeAge(d, now);
  const abs = format(d, "yyyy-MM-dd HH:mm");

  // eslint-disable-next-line @typescript-eslint/no-use-before-define -- module-level const, initialised before any component renders
  const stripeColor = STATUS_STRIPE[session.status] ?? brand.line;
  const title = displaySessionTitle(t, session.title);
  const hasDocs = session.document_count > 0;

  return (
    <ListItem disableGutters sx={{ p: 0, mb: 0.5 }}>
      <Box
        sx={{
          width: "100%",
          position: "relative",
          border: 1,
          borderColor: selected ? "primary.main" : "divider",
          borderLeftWidth: 4,
          borderLeftColor: stripeColor,
          borderRadius: 1,
          bgcolor: selected ? "action.selected" : "background.paper",
          overflow: "hidden",
          "&:hover .session-actions, &:focus-within .session-actions": { opacity: 1 },
        }}
      >
        <Box
          component={Link}
          to={`/app/sessions/${session.id}`}
          sx={{
            display: "block",
            px: 1.25,
            py: 1,
            textDecoration: "none",
            color: "inherit",
          }}
        >
          <Stack
            direction="row"
            sx={{
              alignItems: "center",
              gap: 0.5,
              // Without hover the actions stay visible over this row's
              // end, so the title stops short of them.
              "@media (hover: none)": { pr: 7 },
            }}
          >
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, flex: 1, minWidth: 0 }}
              noWrap
              title={title}
            >
              {title}
            </Typography>
            {session.status !== "draft" && session.status !== "active" && (
              <Chip
                size="small"
                variant="outlined"
                label={t(`session_status.${session.status}`, session.status)}
                sx={{ height: 18, fontSize: 10 }}
              />
            )}
          </Stack>
          {(() => {
            // One line per linked project (primary + M13-F context
            // refs), sorted alphabetically. Keeps the card compact by
            // capping to 3 visible rows + a "+N more" tail.
            const items: Array<{ path: string; branch: string | null }> = [];
            if (session.repository) {
              items.push({
                path: session.repository.path_with_namespace,
                branch: session.branch_name,
              });
            } else if (session.branch_name) {
              items.push({ path: "—", branch: session.branch_name });
            }
            for (const ref of session.context_project_refs ?? []) {
              items.push({
                path: ref.path_with_namespace,
                branch: ref.branch_name,
              });
            }
            items.sort((a, b) => {
              const p = a.path.localeCompare(b.path, undefined, {
                sensitivity: "base",
              });
              if (p !== 0) return p;
              return (a.branch ?? "").localeCompare(b.branch ?? "", undefined, {
                sensitivity: "base",
              });
            });
            if (items.length === 0) return null;
            const visible = items.slice(0, 3);
            const extra = items.length - visible.length;
            return (
              <Stack sx={{ mt: 0.25 }}>
                {visible.map((item, idx) => (
                  <Typography
                    key={`${item.path}:${item.branch}:${idx}`}
                    variant="caption"
                    color="text.secondary"
                    noWrap
                    sx={{ display: "block", fontFamily: "monospace" }}
                  >
                    {item.path}
                    {item.branch ? ` · ${item.branch}` : ""}
                  </Typography>
                ))}
                {extra > 0 && (
                  <Typography
                    variant="caption"
                    color="text.disabled"
                    sx={{ display: "block", fontFamily: "monospace" }}
                  >
                    {t("session.more_projects", { count: extra })}
                  </Typography>
                )}
              </Stack>
            );
          })()}
          <Stack
            direction="row"
            sx={{ alignItems: "center", gap: 0.5, mt: 0.25 }}
          >
            {/* Single muted meta line — dot-separated `intent · mode ·
                LANG` keeps the card compact and the three fragments
                share a uniform visual weight. Chips looked busy with
                one coloured and two neutral. */}
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              sx={{ flex: 1, minWidth: 0 }}
            >
              {metaLine(
                session,
                t(`intents.${session.intent}`, {
                  defaultValue: session.intent,
                }),
                showOrg,
              )}
            </Typography>
            <Tooltip title={abs}>
              <Typography variant="caption" color="text.secondary">
                {rel}
              </Typography>
            </Tooltip>
          </Stack>
          {/* Sharing line — only renders when the session has at
              least one collaborator, so solo sessions stay quiet.
              Lays out as: GroupIcon · "Owner: <name>" · "+N" ·
              "Last edit: <name>" — each fragment optional, joined
              by middle-dots. ``GroupIcon`` doubles as a visual
              "this is shared" tell so a quick scroll picks them
              out without reading. */}
          {isShared && (
            <Stack
              direction="row"
              sx={{
                alignItems: "center",
                gap: 0.5,
                mt: 0.25,
                color: "text.secondary",
              }}
            >
              <GroupOutlinedIcon
                sx={{ fontSize: 13, color: "text.secondary" }}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                sx={{ flex: 1, minWidth: 0 }}
              >
                {ownerLabel
                  ? t("session.shared.owner_label", { name: ownerLabel })
                  : t("session.shared.shared_chip", "Shared")}
                {collaborators.length > 0 && (
                  <>
                    {" · "}
                    {t("session.shared.collab_count", {
                      count: collaborators.length,
                    })}
                  </>
                )}
                {lastActor && (
                  <>
                    {" · "}
                    {t("session.shared.last_actor", { name: lastActor })}
                  </>
                )}
              </Typography>
            </Stack>
          )}
        </Box>

        {/* The documents chip has a row of its own only when there are
            documents; the archive / delete actions float over the title
            row's end (on hover or keyboard focus with a mouse, always
            without one), so a card without documents ends right under
            its last line instead of over an empty action row. */}
        {hasDocs && (
          <Stack
            direction="row"
            sx={{
              alignItems: "center",
              gap: 0.25,
              px: 0.5,
              pb: 0.5,
              pt: 0,
            }}
          >
            <Tooltip
              title={t(
                session.document_count === 1
                  ? "session.document_count_one"
                  : "session.document_count_other",
                { count: session.document_count },
              )}
            >
              <Chip
                size="small"
                icon={<DescriptionOutlinedIcon sx={{ fontSize: 14 }} />}
                label={session.document_count}
                onClick={(e) => {
                  e.preventDefault();
                  setDocsOpen((v) => !v);
                }}
                clickable
                variant={docsOpen ? "filled" : "outlined"}
                sx={{
                  height: 20,
                  fontSize: 11,
                  "& .MuiChip-icon": { marginLeft: 0.5 },
                }}
                deleteIcon={
                  <ExpandMoreIcon
                    sx={{
                      fontSize: 14,
                      transform: docsOpen ? "rotate(180deg)" : undefined,
                      transition: "transform 120ms",
                    }}
                  />
                }
                onDelete={(e) => {
                  e.preventDefault();
                  setDocsOpen((v) => !v);
                }}
              />
            </Tooltip>
          </Stack>
        )}
        <Stack
          direction="row"
          className="session-actions"
          sx={{
            position: "absolute",
            top: 4,
            right: 4,
            zIndex: 1,
            gap: 0,
            borderRadius: 1,
            opacity: 0,
            transition: "opacity 120ms",
            // Opaque, in the card's own colour, so the title under it
            // doesn't show through.
            bgcolor: "background.paper",
            backgroundImage: (theme) =>
              selected
                ? `linear-gradient(${theme.palette.action.selected}, ${theme.palette.action.selected})`
                : "none",
            "@media (hover: none)": { opacity: 1 },
          }}
        >
          {session.status === "archived" ? (
            <Tooltip title={t("common.unarchive", "Unarchive")}>
              <IconButton
                size="small"
                aria-label={t("common.unarchive", "Unarchive")}
                disabled={unarchive.isPending}
                onClick={async (e) => {
                  e.preventDefault();
                  await unarchive.mutateAsync(session.id);
                }}
              >
                <UnarchiveIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title={t("session.archive_tooltip")}>
              <IconButton
                size="small"
                disabled={archive.isPending}
                onClick={async (e) => {
                  e.preventDefault();
                  if (
                    !(await confirm({
                      title: t("session.confirm_archive_title"),
                      message: t("session.confirm_archive_message", {
                        title,
                      }),
                      confirmLabel: t("common.archive"),
                    }))
                  )
                    return;
                  await archive.mutateAsync(session.id);
                }}
              >
                <Inventory2OutlinedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title={t("session.delete_tooltip")}>
            <IconButton
              size="small"
              color="error"
              disabled={deleteSession.isPending}
              onClick={async (e) => {
                e.preventDefault();
                if (
                  !(await confirm({
                    title: t("session.confirm_delete_title"),
                    message: t("session.confirm_delete_message", {
                      title,
                    }),
                    confirmLabel: t("common.delete"),
                    destructive: true,
                  }))
                )
                  return;
                await deleteSession.mutateAsync(session.id);
                if (selected) navigate("/app/sessions");
              }}
            >
              <DeleteIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Stack>

        <Collapse in={docsOpen} unmountOnExit>
          <Stack
            sx={{
              px: 1,
              pb: 1,
              gap: 0.5,
              bgcolor: "background.default",
              borderTop: 1,
              borderColor: "divider",
            }}
          >
            {documents.isLoading && <Skeleton height={24} />}
            {documents.data?.length === 0 && (
              <Typography variant="caption" color="text.secondary">
                {t("session.documents_empty_inline")}
              </Typography>
            )}
            {/* One row per document: its latest version (re-renders of
             *  a draft are versions, not more documents). */}
            {latestVersions(documents.data ?? []).map(({ doc }) => (
              <Stack
                key={doc.id}
                direction="row"
                sx={{ alignItems: "center", gap: 0.5 }}
              >
                {/* Format-specific icon (pdf / docx / pptx) so the
                 *  user can distinguish a deck from a doc at a
                 *  glance instead of relying on the filename alone. */}
                <DocumentFormatIcon
                  format={doc.artifact_format}
                  size={14}
                />
                <Typography
                  variant="caption"
                  noWrap
                  title={doc.title}
                  sx={{ flex: 1, minWidth: 0 }}
                >
                  {doc.title}
                </Typography>
                {doc.version > 1 && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ flexShrink: 0, fontWeight: 600 }}
                  >
                    <VersionLabel version={doc.version} />
                  </Typography>
                )}
                <Tooltip
                  title={t("session.download_format", {
                    format: (doc.available_formats?.[0] ?? "pdf").toUpperCase(),
                  })}
                >
                  <IconButton
                    size="small"
                    component="a"
                    href={documentDownloadHref(doc.id, doc.available_formats?.[0] ?? "pdf")}
                    onClick={(e) => e.stopPropagation()}
                    sx={{ p: 0.25 }}
                  >
                    <DownloadIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              </Stack>
            ))}
          </Stack>
        </Collapse>
      </Box>
    </ListItem>
  );
}

const STATUS_STRIPE: Record<string, string> = {
  active: brand.success,
  draft: brand.muted,
  archived: brand.disabled,
  failed: brand.danger,
};
